// src/pages/payments/PaymentsListPage/PaymentsListPage.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import PaymentsAPI from '../../../api/v3/payments-api-v3';

const BASE = '/app/sales/bagspa';
const API_BASE = process.env.REACT_APP_API_BASE;

function formatIDR(num) {
  return 'Rp ' + Number(num || 0).toLocaleString('id-ID');
}

const METHOD_LABEL = {
  bank_transfer: 'Bank Transfer',
  virtual_account: 'Virtual Account',
  debit_card: 'EDC (Debit)',
  visa: 'EDC (VISA)',
  mastercard: 'EDC (Mastercard)',
  amex: 'EDC (Amex)',
  others: 'Others',
};

const TYPE_LABEL = {
  down_payment: 'Down Payment',
  full_payment: 'Full Payment',
};

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'awaiting_review', label: 'Awaiting Review' },
  { value: 'complete_dp', label: 'Complete DP' },
  { value: 'complete_full', label: 'Complete Full' },
  { value: 'failed', label: 'Failed' },
  { value: 'canceled', label: 'Canceled' },
];

export default function PaymentsListPage() {
  const history = useHistory();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // filters (defaults)
  const [filter, setFilter] = useState({
    q: '',
    status: 'pending',
    from: '',
    to: '',
  });

  // pagination
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(25); // 10 / 25 / 50 / 100

  const load = useCallback(async () => {
  try {
    setLoading(true);
    setErr('');

    const { status } = filter;
    const json = await PaymentsAPI.list({ status });
      const data = json?.items || json?.data || [];
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [filter.status]);

  useEffect(() => { load(); }, [load]);

  // Reset to first page when filters change
  useEffect(() => { setPage(1); }, [filter]);

  // client-side filter for q + date range (created_at)
  const filtered = useMemo(() => {
    const q = filter.q.trim().toLowerCase();
    const fromMs = filter.from ? Date.parse(filter.from + 'T00:00:00') : null;
    const toMs   = filter.to   ? Date.parse(filter.to   + 'T23:59:59') : null;

    return rows.filter(p => {
      // q matches order_no or customer name
      const matchQ = !q ||
        p.order_no?.toLowerCase().includes(q) ||
        p.customer?.name?.toLowerCase().includes(q);

      // created_at in [from, to]
      const createdMs = p.created_at ? Date.parse(p.created_at) : null;
      const matchFrom = fromMs == null || (createdMs != null && createdMs >= fromMs);
      const matchTo   = toMs   == null || (createdMs != null && createdMs <= toMs);

      return matchQ && matchFrom && matchTo;
    });
  }, [rows, filter]);

  // pagination slices
  const totalPages = Math.max(1, Math.ceil(filtered.length / per));
  const pageSafe = Math.min(page, totalPages);
  const startIdx = (pageSafe - 1) * per;
  const paged = useMemo(() => filtered.slice(startIdx, startIdx + per), [filtered, startIdx, per]);

  function exportFile(format = 'csv') {
    const url = new URL(`${API_BASE}/api/v3/payments/export`);
    if (format) url.searchParams.set('format', format);
    if (filter.status) url.searchParams.set('status', filter.status);
    if (filter.from)   url.searchParams.set('from',   filter.from);
    if (filter.to)     url.searchParams.set('to',     filter.to);
    window.open(url.toString());
  }

  function clearFilters() {
    setFilter({ q: '', status: 'pending', from: '', to: '' });
  }

  return (
    <div className="bagspa-page payments-page">
      <div className="bagspa-page-header payments-page-header">
        <div>
          <h3 className="mb-0">Payments List</h3>
        </div>
      </div>

      {/* Controls */}
      <div className="bagspa-filter-card payments-control-card">
        {/* TOP ROW */}
        <div className="payments-control-top-row">
          <div className="bagspa-filter-field payments-filter-search">
            <label className="form-label">Search</label>
            <input
              type="text"
              className="form-control"
              placeholder="Customer or Order No (optional)"
              value={filter.q}
              onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
            />
          </div>

          <div className="bagspa-filter-field payments-filter-status">
            <label className="form-label">Status</label>
            <select
              className="form-control"
              value={filter.status}
              onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="payments-date-group">
            <div className="bagspa-filter-field payments-filter-from">
              <label className="form-label">From</label>
              <input
                type="date"
                className="form-control"
                value={filter.from}
                onChange={(e) => setFilter((f) => ({ ...f, from: e.target.value }))}
              />
            </div>

            <div className="bagspa-filter-field payments-filter-to">
              <label className="form-label">To</label>
              <input
                type="date"
                className="form-control"
                value={filter.to}
                onChange={(e) => setFilter((f) => ({ ...f, to: e.target.value }))}
              />
            </div>
          </div>

          <div className="payments-top-actions">
            <button type="button" className="btn btn-primary" onClick={load}>
              Apply
            </button>

            <button type="button" className="btn btn-outline-primary" onClick={clearFilters}>
              Reset
            </button>
          </div>
        </div>

        {/* BOTTOM ROW */}
        <div className="payments-control-bottom-row">
          <div className="payments-bottom-left">
            <div className="bagspa-filter-field payments-filter-rows">
              <label className="form-label">Rows</label>
              <select
                className="form-control payments-per-select"
                value={per}
                onChange={(e) => {
                  setPer(parseInt(e.target.value, 10) || 25);
                  setPage(1);
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}/page
                  </option>
                ))}
              </select>
            </div>

            <button type="button" className="btn btn-outline-secondary" onClick={load}>
              Refresh
            </button>
          </div>

          <div className="payments-bottom-right">
            <button type="button" className="btn btn-outline-primary" onClick={() => exportFile('csv')}>
              Export CSV
            </button>

            <button type="button" className="btn btn-primary" onClick={() => exportFile('xlsx')}>
              Export XLSX
            </button>
          </div>
        </div>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}

      {loading ? (
        <div>Loading…</div>
      ) : (
        <div className="bagspa-table-card payments-table-card">
          <div className="table-responsive">
            <table className="table table-sm table-bordered align-middle payments-table">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 56 }}>#</th>
                  <th>Order No</th>
                  <th>Customer</th>
                  <th>Method</th>
                  <th>Payment Type</th>
                  <th>Status</th>
                  <th className="text-end">Initial</th>
                  <th className="text-end">Final</th>
                  <th className="text-end">Advance</th>
                  <th className="text-end">Outstanding</th>
                  <th className="text-end">This Payment</th>
                  <th>Date</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {paged.length > 0 ? (
                  paged.map((p, i) => {
                    const s = p.order_summary || {};
                    const initial = Number(s.initial_fixed_price || 0);
                    const finalPrice = Number(s.final_fixed_price || 0);
                    const advance = Number(s.advance_payment_price || 0);
                    const outstanding =
                      typeof s.outstanding_balance !== 'undefined'
                        ? Number(s.outstanding_balance || 0)
                        : Math.max(0, finalPrice - advance);

                    const statusBadge =
                      (p.status || '').includes('complete')
                        ? 'bg-success'
                        : p.status === 'failed' || p.status === 'canceled'
                          ? 'bg-danger'
                          : p.status === 'awaiting_review'
                            ? 'bg-warning text-dark'
                            : 'bg-secondary';

                    return (
                      <tr key={p.id}>
                        <td>{startIdx + i + 1}</td>
                        <td className="text-monospace">{p.order_no}</td>
                        <td>{p.customer?.name || '-'}</td>
                        <td>{METHOD_LABEL[p.payment_method] || p.payment_method || '-'}</td>
                        <td>{TYPE_LABEL[p.payment_type] || p.payment_type || '-'}</td>
                        <td>
                          <span className={`badge ${statusBadge}`}>{p.status}</span>
                        </td>
                        <td className="text-end">{formatIDR(initial)}</td>
                        <td className="text-end">{formatIDR(finalPrice)}</td>
                        <td className="text-end">{formatIDR(advance)}</td>
                        <td className="text-end">
                          {formatIDR(outstanding)}
                          {outstanding <= 0 ? (
                            <span className="ms-1 badge bg-success">PAID</span>
                          ) : null}
                        </td>
                        <td className="text-end fw-medium">{formatIDR(p.amount)}</td>
                        <td>
                          {p.created_at
                            ? new Date(p.created_at).toLocaleDateString('id-ID', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              })
                            : '-'}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => history.push(`${BASE}/payments/${p.id}`)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={13} className="text-center text-muted">
                      No payments found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="bagspa-pagination-row payments-pagination-row">
            <div className="text-muted small">
              Total: {filtered.length} • Page {pageSafe} / {totalPages}
            </div>

            <div className="btn-group">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe <= 1}
                onClick={() => setPage(1)}
              >
                &laquo;
              </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                &lsaquo;
              </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                &rsaquo;
              </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe >= totalPages}
                onClick={() => setPage(totalPages)}
              >
                &raquo;
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
